/**
 * KUINI Premium Manuka Honey Website
 * Main JavaScript Application
 *
 * Features:
 * - Internationalization (Arabic/English) with RTL support
 * - Dark/Light theme toggle
 * - Visitor analytics and tracking
 * - Contact form with ZeptoMail integration
 * - Smooth scrolling and animations
 * - Google Sheets integration for analytics
 */

// Import required modules
import './style.css'
import { initI18n, changeLanguage, t, getCurrentLanguage, isRTL, updateUITranslations, createLanguageSwitcher } from './i18n.js'
import { initializeProgressiveImageLoading, preloadCriticalImages } from './services/imageOptimization.js'

// Global application state
const AppState = {
    isInitialized: false,
    currentTheme: 'honey',
    currentLanguage: 'en',
    visitorData: null
};

/**
 * Initialize the application
 * Sets up internationalization, theme, and analytics
 */
async function initApp() {
    try {
        console.log('🚀 Initializing KUINI website...');

        // Initialize internationalization
        const i18nSuccess = await initI18n();
        if (!i18nSuccess) {
            console.warn('⚠️ i18n initialization failed, continuing with default language');
        }

        // Initialize theme system
        initThemeSystem();

        // Initialize visitor tracking (if enabled)
        if (import.meta.env.VITE_ENABLE_VISITOR_TRACKING === 'true') {
            await initVisitorTracking();
        }

        // Analytics are handled via Google Apps Script tracking

        // Initialize UI components with lazy loading optimization
        initSmoothScrolling();
        initScrollAnimations();
        initContactForm();
        initLanguageSwitcher();
        initThemeToggle();
        initCompanyNameVisibility();
        initSocialMediaLinks();

        // Initialize progressive image loading for better Core Web Vitals
        await initializeProgressiveImageLoading();

        // Preload critical images for LCP optimization
        preloadCriticalImages(['/logo.png']);

        // Initialize lazy loading for analytics and email services
        initLazyServiceLoading();

        // Update UI with current translations
        updateUITranslations();

        AppState.isInitialized = true;
        AppState.currentLanguage = getCurrentLanguage();

        console.log('✅ KUINI website initialized successfully!');
        console.log(`📍 Language: ${AppState.currentLanguage}, Theme: ${AppState.currentTheme}, RTL: ${isRTL()}`);

        // Remove FOUC by showing content when fully loaded
        document.body.classList.add('loaded');

    } catch (error) {
        console.error('❌ Failed to initialize KUINI website:', error);
        // Show content even on error to prevent permanent blank page
        document.body.classList.add('loaded');
    }
}

/**
 * Theme System Management
 * Handles dark/light theme switching with user preference persistence
 */
function initThemeSystem() {
    // Load saved theme or default to honey theme
    const savedTheme = localStorage.getItem('kuini-theme') || 'honey';
    setTheme(savedTheme);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
        if (!localStorage.getItem('kuini-theme')) {
            setTheme(e.matches ? 'dark' : 'honey');
        }
    });
}

/**
 * Set the application theme
 * @param {string} theme - Theme name ('honey', 'dark')
 */
function setTheme(theme) {
    document.documentElement.setAttribute('data-theme', theme);
    AppState.currentTheme = theme;
    localStorage.setItem('kuini-theme', theme);

    // Update theme toggle button if it exists
    const themeToggle = document.getElementById('theme-toggle');
    if (themeToggle) {
        updateThemeToggleButton(themeToggle, theme);
    }

    console.log(`🎨 Theme changed to: ${theme}`);
}

/**
 * Toggle between light and dark themes
 */
function toggleTheme() {
    const newTheme = AppState.currentTheme === 'dark' ? 'honey' : 'dark';
    setTheme(newTheme);
}

/**
 * Update theme toggle button appearance
 * @param {HTMLElement} button - Theme toggle button element
 * @param {string} theme - Current theme
 */
function updateThemeToggleButton(button, theme) {
    const isDark = theme === 'dark';
    button.innerHTML = `
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            ${isDark
                ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"></path>'
                : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>'
            }
        </svg>
    `;
    button.title = isDark ? t('theme.light') : t('theme.dark');
}

/**
 * Initialize Company Name Visibility
 * Controls whether company name appears in header based on env configuration
 */
function initCompanyNameVisibility() {
    try {
        const showCompanyName = import.meta.env.VITE_SHOW_COMPANY_NAME === 'true';
        const companyName = import.meta.env.VITE_COMPANY_NAME || 'KUINI';

        const companyNameElement = document.getElementById('company-name');
        const footerCompanyName = document.querySelector('footer .font-bold.text-xl');

        if (companyNameElement) {
            if (showCompanyName) {
                companyNameElement.textContent = companyName;
                // Show with responsive classes - hidden on mobile, visible on larger screens
                companyNameElement.className = companyNameElement.className.replace(/\bhidden\b/g, '').trim();
                companyNameElement.className += ' hidden sm:inline';
                companyNameElement.style.display = '';
            } else {
                // When disabled, ensure it stays hidden completely
                companyNameElement.className = companyNameElement.className.replace(/\bsm:inline\b/g, '').trim();
                if (!companyNameElement.className.includes('hidden')) {
                    companyNameElement.className += ' hidden';
                }
            }
        }

        if (footerCompanyName) {
            if (showCompanyName) {
                footerCompanyName.textContent = companyName;
                footerCompanyName.style.display = '';
            } else {
                footerCompanyName.style.display = 'none';
            }
        }

        console.log(`🏢 Company name visibility: ${showCompanyName ? 'visible' : 'hidden'} (${companyName})`);

    } catch (error) {
        console.error('❌ Failed to initialize company name visibility:', error);
    }
}

/**
 * Initialize Social Media Links
 * Connects footer social media icons to URLs from environment configuration
 */
function initSocialMediaLinks() {
    try {
        const twitterUrl = import.meta.env.VITE_TWITTER_URL;
        const facebookUrl = import.meta.env.VITE_FACEBOOK_URL;
        const pinterestUrl = import.meta.env.VITE_PINTEREST_URL;

        // Connect Twitter link
        const twitterLink = document.getElementById('twitter-link');
        if (twitterLink && twitterUrl) {
            twitterLink.href = twitterUrl;
        } else if (twitterLink) {
            twitterLink.style.display = 'none';
        }

        // Connect Facebook link
        const facebookLink = document.getElementById('facebook-link');
        if (facebookLink && facebookUrl) {
            facebookLink.href = facebookUrl;
        } else if (facebookLink) {
            facebookLink.style.display = 'none';
        }

        // Connect Pinterest link
        const pinterestLink = document.getElementById('pinterest-link');
        if (pinterestLink && pinterestUrl) {
            pinterestLink.href = pinterestUrl;
        } else if (pinterestLink) {
            pinterestLink.style.display = 'none';
        }

        console.log('🔗 Social media links initialized:', {
            twitter: twitterUrl || 'Not configured',
            facebook: facebookUrl || 'Not configured',
            pinterest: pinterestUrl || 'Not configured'
        });

    } catch (error) {
        console.error('❌ Failed to initialize social media links:', error);
    }
}

/**
 * Visitor Tracking System
 * Collects visitor information including IP, location, and behavior
 */
async function initVisitorTracking() {
    try {
        console.log('📊 Initializing visitor tracking...');

        // Get visitor location data
        const locationData = await getVisitorLocation();

        // Get browser and device information
        const deviceData = getBrowserInfo();

        // Combine all visitor data
        AppState.visitorData = {
            ...locationData,
            ...deviceData,
            timestamp: new Date().toISOString(),
            sessionId: generateSessionId(),
            isReturningVisitor: checkReturningVisitor(),
            referrer: document.referrer || 'Direct',
            userAgent: navigator.userAgent
        };

        // Store visitor session
        storeVisitorSession();

        // Send to Google Apps Script if enabled
        if (import.meta.env.VITE_ENABLE_APPS_SCRIPT_TRACKING === 'true') {
            await sendVisitorDataToSheets();
        }

        console.log('✅ Visitor tracking initialized:', AppState.visitorData);

    } catch (error) {
        console.error('❌ Visitor tracking failed:', error);
    }
}

/**
 * Get visitor's geographic location using IP
 * @returns {Object} Location data
 */
async function getVisitorLocation() {
    try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        return {
            ip: data.ip,
            country: data.country_name,
            city: data.city,
            region: data.region,
            latitude: data.latitude,
            longitude: data.longitude,
            timezone: data.timezone,
            isp: data.org
        };
    } catch (error) {
        console.warn('⚠️ Failed to get location data:', error);
        return {
            ip: 'Unknown',
            country: 'Unknown',
            city: 'Unknown',
            region: 'Unknown'
        };
    }
}

/**
 * Get browser and device information
 * @returns {Object} Browser/device data
 */
function getBrowserInfo() {
    return {
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${screen.width}x${screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        cookiesEnabled: navigator.cookieEnabled,
        onlineStatus: navigator.onLine
    };
}

/**
 * Generate a unique session ID
 * @returns {string} Session ID
 */
function generateSessionId() {
    return 'kuini_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

/**
 * Check if visitor is returning
 * @returns {boolean} True if returning visitor
 */
function checkReturningVisitor() {
    const lastVisit = localStorage.getItem('kuini-last-visit');
    const isReturning = !!lastVisit;
    localStorage.setItem('kuini-last-visit', new Date().toISOString());
    return isReturning;
}

/**
 * Store visitor session data locally
 */
function storeVisitorSession() {
    const sessions = JSON.parse(localStorage.getItem('kuini-sessions') || '[]');
    sessions.push({
        sessionId: AppState.visitorData.sessionId,
        timestamp: AppState.visitorData.timestamp,
        data: AppState.visitorData
    });

    // Keep only last 10 sessions
    if (sessions.length > 10) {
        sessions.splice(0, sessions.length - 10);
    }

    localStorage.setItem('kuini-sessions', JSON.stringify(sessions));
}

/**
 * Send visitor data to Google Sheets
 * Requires Google Sheets API to be configured
 */
async function sendVisitorDataToSheets() {
    try {
        // Import analytics service dynamically
        const { sendVisitorDataToSheets } = await import('./services/analytics.js');

        // Send visitor data to Google Sheets
        const success = await sendVisitorDataToSheets(AppState.visitorData);

        if (success) {
            console.log('📈 Visitor data sent to Google Sheets successfully');
        } else {
            console.log('📈 Google Sheets not configured, visitor data logged locally');
        }

    } catch (error) {
        console.error('❌ Failed to send data to Google Sheets:', error);
    }
}

/**
 * Initialize Lazy Service Loading
 * Preload services that might be needed later for better performance
 */
function initLazyServiceLoading() {
    // Lazy load analytics service when user interacts with page
    const lazyLoadAnalytics = () => import('./services/analytics.js');
    const lazyLoadEmail = () => import('./services/email.js');

    // Preload on first user interaction for better performance
    const userInteractionEvents = ['click', 'touchstart', 'keydown'];
    const preloadServices = () => {
        if (import.meta.env.VITE_ENABLE_APPS_SCRIPT_TRACKING === 'true') {
            lazyLoadAnalytics();
        }
        if (import.meta.env.VITE_ENABLE_ZEPTOMAIL === 'true') {
            lazyLoadEmail();
        }

        // Remove listeners after first interaction
        userInteractionEvents.forEach(event => {
            document.removeEventListener(event, preloadServices, { once: true, passive: true });
        });
    };

    // Add interaction listeners for service preloading
    userInteractionEvents.forEach(event => {
        document.addEventListener(event, preloadServices, { once: true, passive: true });
    });

    console.log('⚡ Lazy service loading initialized');
}

/**
 * Smooth Scrolling for Navigation Links
 * Handles anchor link navigation with smooth scrolling animation
 */
function initSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            e.preventDefault();
            const target = document.querySelector(this.getAttribute('href'));
            if (target) {
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });

                // Navigation tracking handled by visitor analytics
            }
        });
    });
}

/**
 * Scroll Animations with Modern IntersectionObserver
 * Adds navbar shadow and triggers element animations on scroll with better performance
 */
function initScrollAnimations() {
    let ticking = false;

    // Modern IntersectionObserver for scroll animations
    const initModernScrollAnimations = () => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate-fade-in-up');
                    observer.unobserve(entry.target); // Stop observing once animated
                }
            });
        }, {
            threshold: 0.1,
            rootMargin: '50px' // Start animation 50px before element enters viewport
        });

        document.querySelectorAll('.card, .stats, .hero-content')
            .forEach(el => observer.observe(el));
    };

    // Optimized scroll handler for navbar effects only
    function updateOnScroll() {
        const navbar = document.querySelector('.navbar');
        const scrollY = window.scrollY;

        // Add/remove navbar shadow with performance optimization
        if (scrollY > 100) {
            navbar?.classList.add('shadow-lg');
        } else {
            navbar?.classList.remove('shadow-lg');
        }

        ticking = false;
    }

    function requestTick() {
        if (!ticking) {
            requestAnimationFrame(updateOnScroll);
            ticking = true;
        }
    }

    // Initialize modern scroll animations
    initModernScrollAnimations();

    // Add optimized scroll listener for navbar effects
    window.addEventListener('scroll', requestTick, { passive: true });
}

/**
 * Toast Notification System
 * Shows success/error messages with animations
 * @param {string} message - Message text
 * @param {string} type - Message type ('success' or 'error')
 */
function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = 'toast toast-top toast-end z-50';

    const alertClass = type === 'error' ? 'alert-error' : 'alert-success';
    const icon = type === 'error' ?
        '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />' :
        '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />';

    toast.innerHTML = `
        <div class="alert ${alertClass} shadow-lg animate-slide-in-right">
            <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">${icon}</svg>
            <span class="font-medium">${message}</span>
        </div>
    `;

    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), type === 'error' ? 4000 : 3000);
}

/**
 * Contact Form Management with Modern Validation
 * Handles form validation and submission with ZeptoMail integration
 */
function initContactForm() {
    const form = document.querySelector('#contact-form');
    if (!form) return;

    console.log('📧 Initializing contact form with modern validation...');

    // Enhanced email validation with modern APIs and RFC compliance
    const validateEmail = (email) => {
        // Use modern HTML5 validation + custom logic
        const basicPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return basicPattern.test(email) && email.length <= 254; // RFC 5321 limit
    };

    // Use Constraint Validation API for better form handling
    const validateWithConstraints = (field) => {
        if (!field.checkValidity()) {
            field.reportValidity();
            return false;
        }
        return true;
    };

    const showError = (field, message) => {
        const existing = field.parentNode.querySelector('.error-message');
        if (existing) existing.remove();

        const error = document.createElement('div');
        error.className = 'error-message text-sm text-error mt-1';
        error.textContent = message;
        field.parentNode.appendChild(error);
        field.classList.add('input-error');
    };

    const clearError = (field) => {
        const existing = field.parentNode.querySelector('.error-message');
        if (existing) existing.remove();
        field.classList.remove('input-error');
    };

    form.addEventListener('submit', async function(e) {
        e.preventDefault();

        const formData = {
            name: this.name.value.trim(),
            email: this.email.value.trim(),
            message: this.message.value.trim()
        };

        const submitButton = this.querySelector('button[type="submit"]');

        // Clear previous errors
        [this.name, this.email, this.message].forEach(clearError);

        // Validation
        let hasErrors = false;

        if (formData.name.length < 2) {
            showError(this.name, t('contact.form.nameError'));
            hasErrors = true;
        }
        if (!validateEmail(formData.email)) {
            showError(this.email, t('contact.form.emailError'));
            hasErrors = true;
        }
        if (formData.message.length < 10) {
            showError(this.message, t('contact.form.messageError'));
            hasErrors = true;
        }

        if (hasErrors) {
            showToast(t('contact.form.error'), 'error');
            return;
        }

        // Show loading state
        submitButton.innerHTML = `<span class="loading loading-spinner loading-sm"></span> ${t('contact.form.sending')}`;
        submitButton.disabled = true;

        try {
            // Send email via ZeptoMail (if enabled)
            let emailSent = false;
            if (import.meta.env.VITE_ENABLE_ZEPTOMAIL === 'true') {
                emailSent = await sendContactEmail(formData);
            } else {
                // Simulate email sending for development
                await new Promise(resolve => setTimeout(resolve, 2000));
                emailSent = true;
            }

            // Send contact data to Google Apps Script (if enabled)
            if (import.meta.env.VITE_ENABLE_APPS_SCRIPT_TRACKING === 'true') {
                try {
                    const { sendContactDataToSheets } = await import('./services/analytics.js');
                    await sendContactDataToSheets({
                        ...formData,
                        ...AppState.visitorData // Include visitor context
                    });
                } catch (error) {
                    console.warn('⚠️ Failed to send contact data to Google Apps Script:', error);
                }
            }

            // Form submission tracking handled by visitor analytics

            if (emailSent) {
                // Show success message
                showToast(t('contact.form.success'));
                this.reset();
            } else {
                showToast('Message received but email delivery failed. We will still respond to your inquiry.', 'warning');
                this.reset();
            }

        } catch (error) {
            console.error('❌ Failed to send contact form:', error);
            showToast('Failed to send message. Please try again.', 'error');
        } finally {
            // Reset button state
            submitButton.innerHTML = t('contact.form.send');
            submitButton.disabled = false;
        }
    });
}

/**
 * Send contact email via ZeptoMail
 * @param {Object} formData - Form data object
 */
async function sendContactEmail(formData) {
    // Import email service dynamically to avoid build issues
    const { sendContactEmail: sendEmail, sendAutoReplyEmail } = await import('./services/email.js');

    try {
        // Send email to KUINI team
        const emailSent = await sendEmail(formData);

        if (emailSent) {
            // Send auto-reply to customer
            await sendAutoReplyEmail(formData);
            return true;
        }

        return false;
    } catch (error) {
        console.error('❌ Email sending failed:', error);
        return false;
    }
}

/**
 * Initialize Language Switcher
 * Sets up the language toggle functionality in the navbar
 */
function initLanguageSwitcher() {
    const navbar = document.querySelector('.navbar-end');
    if (!navbar) return;

    // Create language switcher container
    const languageSwitcherContainer = document.createElement('div');
    languageSwitcherContainer.id = 'language-switcher';
    languageSwitcherContainer.className = 'mr-2';
    languageSwitcherContainer.innerHTML = createLanguageSwitcher();

    // Insert before the "Get In Touch" button
    const getInTouchBtn = navbar.querySelector('a[href="#contact"]');
    if (getInTouchBtn) {
        navbar.insertBefore(languageSwitcherContainer, getInTouchBtn);
    } else {
        navbar.appendChild(languageSwitcherContainer);
    }

    console.log('🌐 Language switcher initialized');
}

/**
 * Initialize Theme Toggle
 * Adds theme toggle button to the navbar
 */
function initThemeToggle() {
    const navbar = document.querySelector('.navbar-end');
    if (!navbar) return;

    // Create theme toggle button
    const themeToggle = document.createElement('button');
    themeToggle.id = 'theme-toggle';
    themeToggle.className = 'theme-toggle mr-2';
    themeToggle.onclick = toggleTheme;

    updateThemeToggleButton(themeToggle, AppState.currentTheme);

    // Insert at the beginning of navbar-end
    navbar.insertBefore(themeToggle, navbar.firstChild);

    console.log('🎨 Theme toggle initialized');
}

/**
 * Global Functions
 * Expose functions to the global scope for HTML onclick handlers
 */
window.toggleTheme = toggleTheme;
window.switchLanguage = async (language) => {
    await changeLanguage(language);
    AppState.currentLanguage = language;
    updateUITranslations();
};

/**
 * Application Initialization
 * Wait for DOM to be ready, then initialize the app
 */
document.addEventListener('DOMContentLoaded', initApp);

/**
 * Language Change Event Listener
 * Update UI when language changes
 */
window.addEventListener('languageChanged', (event) => {
    console.log('🌐 Language changed event:', event.detail);
    updateUITranslations();
});

// Export for testing and external access
export {
    AppState,
    showToast,
    toggleTheme,
    initApp
};